import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

export default function AITutor() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm your OSHA BBP AI Assistant. Ask me anything about bloodborne pathogens, PPE, OSHA rules, or the course material.",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  if (!user) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/tutor/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      if (!res.ok || !res.body) throw new Error("Chat failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.replace(/^data:\s*/, "");
          try {
            const obj = JSON.parse(payload);
            if (obj.session_id && !sessionId) setSessionId(obj.session_id);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
            if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Sorry — I hit an error. Please try again." };
                return copy;
              });
            }
          } catch (err) {
            console.error("Tutor SSE frame parse failed", err);
          }
        }
      }
    } catch (err) {
      console.error("Tutor chat failed", err);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry — I couldn't reach the tutor. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="ai-tutor-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-zinc-900 text-white pl-4 pr-5 py-3 rounded-full shadow-lg border border-zinc-800 hover:bg-red-600 hover:border-red-600 transition-colors flex items-center gap-2 group"
        >
          <Sparkles className="h-4 w-4" />
          <span className="text-sm font-medium">Ask AI Tutor</span>
        </button>
      )}

      {open && (
        <div
          data-testid="ai-tutor-panel"
          className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] bg-white border border-zinc-900 rounded-lg shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 bg-zinc-900 text-white">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded bg-red-600 flex items-center justify-center">
                <MessageSquare className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-bold font-display">OSHA AI Assistant</div>
                <div className="text-[10px] text-zinc-400 uppercase tracking-wider">Claude Sonnet 4.5</div>
              </div>
            </div>
            <button
              data-testid="ai-tutor-close-btn"
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50" data-testid="ai-tutor-messages">
            {messages.map((m, i) => (
              <div
                key={`msg-${i}-${m.role}`}
                className={`text-sm ${m.role === "user" ? "flex justify-end" : "flex justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-md whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-zinc-900 text-white"
                      : "bg-white border border-zinc-200 text-zinc-800"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <form
            className="border-t border-zinc-200 p-3 flex gap-2 bg-white"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <Input
              data-testid="ai-tutor-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about OSHA, HBV, PPE…"
              disabled={streaming}
              className="text-sm"
            />
            <Button
              type="submit"
              data-testid="ai-tutor-send-btn"
              disabled={streaming || !input.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
